/**
 * Minimaler Markdown-Renderer für Lernreihen-Inhalte (Issue #11).
 *
 * Rendert ausschließlich React-Elemente — kein dangerouslySetInnerHTML, damit
 * auch künftig nutzergenerierte Inhalte kein XSS-Risiko darstellen.
 *
 * Unterstützter Umfang (bewusst klein gehalten):
 *   **fett**            → <strong>
 *   `code`              → <code>
 *   > Zitat             → <blockquote>
 *   | Tabelle | …       → <pre> (Rohdarstellung wie bisher)
 *   ```Codeblock```     → <pre>
 */

/** Zerlegt eine Zeile in Text-, Fett- und Code-Abschnitte. */
export function parseInline(text) {
  const tokens = [];
  const pattern = /\*\*([^*]+)\*\*|`([^`]+)`/g;
  let last = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) tokens.push({ type: 'text', value: text.slice(last, match.index) });
    if (match[1] !== undefined) tokens.push({ type: 'strong', value: match[1] });
    else tokens.push({ type: 'code', value: match[2] });
    last = match.index + match[0].length;
  }
  if (last < text.length) tokens.push({ type: 'text', value: text.slice(last) });
  return tokens;
}

function Inline({ text }) {
  return (
    <>
      {parseInline(text).map((t, i) => {
        if (t.type === 'strong') return <strong key={i}>{t.value}</strong>;
        if (t.type === 'code') return <code key={i} className="bg-ocean-50 rounded px-1 text-[0.9em]">{t.value}</code>;
        return <span key={i}>{t.value}</span>;
      })}
    </>
  );
}

/** Rendert einen Absatz (Block) des Quelltexts. */
function Block({ text }) {
  if (text.startsWith('|')) {
    return (
      <pre className="text-xs bg-ocean-50 rounded-lg p-3 overflow-x-auto whitespace-pre border border-ocean-100">
        {text}
      </pre>
    );
  }

  if (text.startsWith('```')) {
    return (
      <pre className="text-xs bg-gray-50 rounded-lg p-3 overflow-x-auto border border-gray-200">
        {text.replace(/^```[a-z]*\n?/, '').replace(/\n?```$/, '')}
      </pre>
    );
  }

  if (text.startsWith('> ')) {
    return (
      <blockquote className="border-l-4 border-ocean-300 pl-3 italic text-gray-600">
        <Inline text={text.replace(/^> /gm, '')} />
      </blockquote>
    );
  }

  return <p className="leading-relaxed"><Inline text={text} /></p>;
}

export default function Markdown({ text, className = '' }) {
  if (!text) return null;
  return (
    <div className={className}>
      {text.split('\n\n').map((para, i) => <Block key={i} text={para} />)}
    </div>
  );
}
